import { Helmet } from 'react-helmet-async'
import { SITE_URL, toAbsoluteUrl } from '@/lib/seo/absoluteUrl'
import {
  BRAND_LOGO_URL,
  BRAND_NAME,
  buildOrganizationJsonLd,
  buildWebSiteJsonLd,
} from '@/lib/seo/siteMeta'

interface FacilityJsonLdProps {
  name: string
  address: string
  addressSido?: string | null
  addressSigungu?: string | null
  phone?: string | null
  latitude?: number | null
  longitude?: number | null
  evaluationGrade?: string | null
  capacity?: number | null
  currentCount?: number | null
  facilityType?: string
  hasDementiaCare?: boolean
  url: string
  reviewCount?: number
  reviewAvgRating?: number
}

const FACILITY_TYPE_MAP: Record<string, string> = {
  NURSING_HOME: '요양원',
  NURSING_HOME_SMALL: '공동생활가정',
  DAY_CARE: '주간보호센터',
  SHORT_STAY: '단기보호센터',
  HOME_VISIT_CARE: '방문요양',
  HOME_VISIT_BATH: '방문목욕',
  HOME_VISIT_NURSING: '방문간호',
  WELFARE_EQUIPMENT: '복지용구',
  DEMENTIA_CARE: '치매전담시설',
  NURSING_HOSPITAL: '요양병원',
  OTHER: '요양시설',
}

export function FacilityJsonLd({
  name, address, addressSido, addressSigungu, phone, latitude, longitude,
  evaluationGrade, capacity, currentCount, facilityType, hasDementiaCare, url,
  reviewCount, reviewAvgRating,
}: FacilityJsonLdProps) {
  const typeName = FACILITY_TYPE_MAP[facilityType ?? ''] || '요양시설'

  const jsonLd: Record<string, any> = {
    '@context': 'https://schema.org',
    '@type': 'MedicalOrganization',
    name,
    description: `${name} - ${addressSido || ''} ${addressSigungu || ''} ${typeName}. 정원 ${capacity || '-'}명${currentCount != null ? `, 현원 ${currentCount}명` : ''}. ${evaluationGrade ? `평가등급 ${evaluationGrade}.` : ''}`,
    address: {
      '@type': 'PostalAddress',
      streetAddress: address,
      addressLocality: addressSigungu || '',
      addressRegion: addressSido || '',
      addressCountry: 'KR',
    },
    url: `https://carebalance.co.kr${url}`,
  }

  if (phone) jsonLd.telephone = phone
  if (hasDementiaCare) jsonLd.medicalSpecialty = '치매전담'
  if (latitude && longitude) {
    jsonLd.geo = {
      '@type': 'GeoCoordinates',
      latitude,
      longitude,
    }
  }
  if (capacity) {
    jsonLd.maximumAttendeeCapacity = capacity
  }
  // AggregateRating 은 사용자 리뷰가 쌓인 후에만 발행한다.
  // NHIS A~E 평가등급은 schema.org 표준(숫자 척도)과 어긋나고, ratingCount/reviewCount
  // 부재로 Google Search Console 이 invalid 로 처리(2026-05-03 진단). 평가등급은
  // description 텍스트에만 노출하고, reviewCount/reviewAvgRating 이 props 으로
  // 넘어올 때만 schema.org 표준 형태로 발행한다.
  if (reviewCount && reviewCount > 0 && reviewAvgRating != null) {
    jsonLd.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: reviewAvgRating,
      bestRating: 5,
      worstRating: 1,
      reviewCount,
    }
  }

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
    </Helmet>
  )
}

// Organization JSON-LD for homepage
// 값의 단일 소스는 lib/seo/siteMeta.ts — 여기서 하드코딩하지 않는다 (structured-data-spec §2.3.3)
export function OrganizationJsonLd() {
  const jsonLd = buildOrganizationJsonLd()

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
    </Helmet>
  )
}

// BreadcrumbList JSON-LD for regional pages
interface BreadcrumbItem {
  name: string
  url?: string
}

export function BreadcrumbJsonLd({ items }: { items: BreadcrumbItem[] }) {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => {
      const entry: Record<string, any> = {
        '@type': 'ListItem',
        position: index + 1,
        name: item.name,
      }
      if (item.url) {
        entry.item = `https://carebalance.co.kr${item.url}`
      }
      return entry
    }),
  }

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
    </Helmet>
  )
}

// FAQPage JSON-LD for regional pages and guide pages
interface FAQItem {
  question: string
  answer: string
}

export function FAQPageJsonLd({ faqs }: { faqs: FAQItem[] }) {
  if (!faqs || faqs.length === 0) return null

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  }

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
    </Helmet>
  )
}

// HowTo JSON-LD for step-by-step guide pages
interface HowToStep {
  name: string
  text: string
}

export function HowToJsonLd({ name, steps }: { name: string; steps: HowToStep[] }) {
  if (!steps || steps.length === 0) return null

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name,
    step: steps.map((s, index) => ({
      '@type': 'HowToStep',
      position: index + 1,
      name: s.name,
      text: s.text,
    })),
  }

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
    </Helmet>
  )
}

// Article JSON-LD for blog posts (영구지침: docs/develop/seo/blog-spec.md §8)
export function ArticleJsonLd({
  headline,
  description,
  image,
  datePublished,
  dateModified,
  author,
  slug,
}: {
  headline: string
  description: string
  image?: string
  datePublished: string
  dateModified?: string
  author: string
  slug: string
}) {
  const jsonLd: Record<string, any> = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline,
    description,
    datePublished,
    dateModified: dateModified ?? datePublished,
    author: { '@type': 'Organization', name: author },
    publisher: {
      '@type': 'Organization',
      name: BRAND_NAME,
      logo: {
        '@type': 'ImageObject',
        // 실제 존재하는 PNG 여야 한다. /logo.png 는 없어서 nginx 가 index.html 로 폴백했다 (blog-spec §8)
        // 흰 로고(carebalance_logo.png)는 흰 배경에서 보이지 않아 파란 로고를 쓴다 (structured-data-spec §2.3.2)
        url: BRAND_LOGO_URL,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${SITE_URL}/blog/${slug}`,
    },
  }
  // image 는 절대 URL — 썸네일이 /api/v1/files/… 상대 경로일 수 있다 (SEO.tsx og:image 와 동일 규칙)
  if (image) jsonLd.image = toAbsoluteUrl(image)

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
    </Helmet>
  )
}

// WebSite JSON-LD with SearchAction for sitelinks search box
// 값의 단일 소스는 lib/seo/siteMeta.ts (structured-data-spec §2.3.3)
export function WebSiteJsonLd() {
  const jsonLd = buildWebSiteJsonLd()

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
    </Helmet>
  )
}
